import { hasValue, isProcessingMicrolearningString } from "./activeCheckpointMicrolearningValidity";

/**
 * Video uploaded on a draft row: `proposed_cloudflarevideo_id` and/or `microlearning` (iframe / id).
 */
export function hasDraftUploadedProposedVideo(checkpoint) {
  if (!checkpoint) return false;
  if (hasValue(checkpoint.proposed_cloudflarevideo_id)) return true;
  const ml = checkpoint.microlearning;
  if (hasValue(ml) && !isProcessingMicrolearningString(String(ml))) return true;
  return false;
}

/**
 * Video is present but proposed kit fields are all empty (protocol, instructions, quiz).
 */
export function isProposedVideoKitIncomplete(checkpoint) {
  if (!hasDraftUploadedProposedVideo(checkpoint)) return false;
  return (
    !hasValue(checkpoint.proposed_inspection_protocol) &&
    !hasValue(checkpoint.proposed_instructions) &&
    !hasValue(checkpoint.proposed_quiz)
  );
}

/**
 * A pending `upload_microlearning_video` task exists for this checkpoint while the
 * assignment signal is populated. `CheckpointUploadTaskAssignmentMixin` only sets
 * `assigned_to` when a PENDING upload task exists, so a non-null `assigned_to` is the
 * definitive signal — no extra API call or field needed.
 */
export function hasPendingUploadTask(checkpoint) {
  return hasValue(checkpoint?.assigned_to);
}

/**
 * Mirrors backend `checkpoint.draft_checkpoint_is_valid()`.
 * When the API omits the field, treat the checkpoint as valid (no UI block).
 */
export function isDraftCheckpointValid(checkpoint) {
  if (!checkpoint || typeof checkpoint !== "object") {
    return true;
  }
  if (!Object.hasOwn(checkpoint, "draft_checkpoint_is_valid")) {
    return true;
  }
  return Boolean(checkpoint.draft_checkpoint_is_valid);
}

/**
 * Whether Activate should be disabled for this checkpoint (draft + invalid).
 */
export function isCheckpointActivateDisabled(checkpoint) {
  const status = String(checkpoint?.status ?? "").toLowerCase();
  if (status !== "draft") {
    return false;
  }
  if (hasPendingUploadTask(checkpoint)) {
    return true;
  }
  if (isProposedVideoKitIncomplete(checkpoint)) {
    return true;
  }
  return !isDraftCheckpointValid(checkpoint);
}

/**
 * Bulk activate: skip draft checkpoints that fail validity.
 */
export function filterCheckpointIdsForBulkActivate(checkpoints, selectedIds) {
  const idSet = new Set(selectedIds ?? []);
  return (checkpoints ?? [])
    .filter((row) => row?.id != null && idSet.has(row.id))
    .filter((row) => {
      const status = String(row.status ?? "").toLowerCase();
      if (status !== "draft") {
        return true;
      }
      if (isProposedVideoKitIncomplete(row)) {
        return false;
      }
      return isDraftCheckpointValid(row);
    })
    .map((row) => row.id);
}
